import React, { useState, useEffect } from "react";
import ChatHeader from "./ChatHeader";
import MessageList from "./MessageList";
import MessageInput from "./MessageInput";
import WhatsAppUniversalTemplatePreview from "./WhatsAppUniversalTemplatePreview"; // Import the preview engine
import { Form, Input, Button, Card, Spin } from "antd";
import { baseUrl } from "../../../config";
import api from "../../../utils/api";

export default function ChatWindow({ chat, messages, onSend, onSendTemplate }) {
  // Track the active selected template layout payload data
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  // Track dynamic field input values typed by the agent for {{1}}, {{2}}
  const [templateVariables, setTemplateVariables] = useState([]);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [sendingTemplate, setSendingTemplate] = useState(false); // NEW: disable button while sending

  // Reset the active template view whenever an agent switches chats
  useEffect(() => {
    setSelectedTemplateId(null);
    setSelectedTemplate(null);
    setTemplateVariables([]);
  }, [chat?.id]);

  useEffect(() => {
  if (!selectedTemplateId) {
    setSelectedTemplate(null);
    return;
  }

  const fetchTemplateDetails = async () => {
    setTemplateLoading(true);
    try {
      // API request passing the dynamic ID parameter path
      const response = await api.get(baseUrl+ `/crm/template/${selectedTemplateId}`);
      setSelectedTemplate(response?.data?.data || response?.data);
    } catch (error) {
      console.error("Error fetching WhatsApp template layout by ID:", error);
    } finally {
      setTemplateLoading(false);
    }
  };

  fetchTemplateDetails();
}, [selectedTemplateId]);

  // Handle local parsing to figure out how many input boxes to render for the agent
  const getVariableCount = (components = []) => {
    const bodyNode = components.find((c) => c.type === "BODY");
    if (!bodyNode || !bodyNode.text) return 0;
    const matches = bodyNode.text.match(/\{\{\d+\}\}/g);
    return matches ? matches.length : 0;
  };

  const variableCount = selectedTemplate ? getVariableCount(selectedTemplate.components) : 0;

  const handleVariableChange = (index, value) => {
    const updated = [...templateVariables];
    updated[index] = value;
    setTemplateVariables(updated);
  };

  // --- CHANGED: now builds a local display bubble and calls onSendTemplate
  // (which lives in useChat) instead of calling api.post directly here.
  // This is what makes the template message actually appear in the chat
  // window locally, the same way normal text messages do.
  const handleFormSubmit = async () => {
    if (!selectedTemplate || sendingTemplate) return;

    // Build the human-readable text the same way the preview panel does,
    // so what shows up in the local chat bubble matches what the
    // recipient actually receives.
    const bodyNode = selectedTemplate?.components?.find((c) => c.type === "BODY");
    let displayText = bodyNode?.text ?? selectedTemplate?.templateName ?? selectedTemplate?.name ?? "";
    templateVariables.forEach((val, index) => {
      displayText = displayText.replace(`{{${index + 1}}}`, val || `{{${index + 1}}}`);
    });

    setSendingTemplate(true);
    try {
      await onSendTemplate({
        templateId: selectedTemplate.id,
        templateName: selectedTemplate.name || selectedTemplate.templateName,
        displayText,
        // chat.id IS the leadId (per your my-chats API), not chat.leadId
        leadId: chat?.id,
        mobile: chat?.mobile,
        textVariables: templateVariables,
      });

      // Only close/reset the panel once the send actually succeeded
      setSelectedTemplateId(null);
      setSelectedTemplate(null);
      setTemplateVariables([]);
    } catch (error) {
      console.error("Failed to execute outbound template dispatch:", error);
      // Keep the panel open on failure so the agent doesn't lose their
      // filled-in variables and can retry.
    } finally {
      setSendingTemplate(false);
    }
  };

  // ── Empty state ───────────────────────────────────────────
  if (!chat) {
    return (
      <div style={styles.empty}>
        <svg width="56" height="56" viewBox="0 0 24 24" fill="none"
          stroke="#cbd5e1" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
        <p style={styles.emptyTitle}>Select a conversation</p>
        <p style={styles.emptySub}>Choose a contact from the left to start chatting</p>
      </div>
    );
  }

  // ── Active chat ───────────────────────────────────────────
  return (
    <div style={styles.panel}>
      {/* 1. Header with custom onTemplateSelect hook callback */}
      <ChatHeader 
        chat={chat} 
        onTemplateSelect={(id) => {
          setSelectedTemplateId(id);
          setTemplateVariables([]); // Clear previous variables when changing templates
        }} 
      />

      {/* Main workspace layer split wrapper section */}
      <div style={styles.mainContentWrapper}>
        
        {/* Left Side: Traditional Conversation Stream Module */}
        <div style={styles.chatStreamArea}>
          <MessageList messages={messages} />
          <MessageInput onSend={onSend} />
        </div>

        {/* Right Side: Sliding Meta Template Composer Side Panel Panel Layer */}
        {selectedTemplate && (
          <div style={styles.templateSidePanel}>
            <div style={styles.sidePanelHeader}>
              <h4 style={styles.panelTitle}>Template Messenger Configuration</h4>
              <Button 
                type="text" 
                danger 
                onClick={() => setSelectedTemplateId(null)}
                style={{ fontSize: "12px" }}
              >
                Cancel
              </Button>
            </div>

          {templateLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}><Spin /></div>
              ) : selectedTemplate && (
            <div style={styles.sidePanelScrollBody}>
              {/* Visual Handset Preview Component Canvas */}
              <div style={styles.previewSpacing}>
                <WhatsAppUniversalTemplatePreview 
                  apiResponse={{ data: selectedTemplate }} 
                  variables={templateVariables}
                />
              </div>

              {/* Dynamic Parameter Variables Field Input Collection Form Card Block */}
              {variableCount > 0 && (
                <Card title="Required Placeholders" size="small" style={styles.formCard}>
                  <Form layout="vertical">
                    {Array.from({ length: variableCount }).map((_, idx) => (
                      <Form.Item 
                        key={idx} 
                        label={`Parameter Variable {{${idx + 1}}}`} 
                        required
                        style={{ marginBottom: "12px" }}
                      >
                        <Input
                          placeholder={`Enter data context value for {{${idx + 1}}}...`}
                          value={templateVariables[idx] || ""}
                          onChange={(e) => handleVariableChange(idx, e.target.value)}
                        />
                      </Form.Item>
                    ))}
                  </Form>
                </Card>
              )}

              {/* Action Sheet Send Button */}
              <Button 
                type="primary" 
                block 
                loading={sendingTemplate}
                disabled={sendingTemplate}
                onClick={handleFormSubmit}
                style={styles.sendBtn}
              >
                Send Approved Template Notice
              </Button>
            </div>
              )}
          </div>
        )}

      </div>
    </div>
  );
}

const styles = {
  panel: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    height: "100%",
    overflow: "hidden",
    minWidth: 0,
  },
  mainContentWrapper: {
    display: "flex",
    flexDirection: "row",
    flex: 1,
    height: "calc(100% - 58px)", // Offsets header block boundaries
    width: "100%",
    overflow: "hidden",
  },
  chatStreamArea: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minWidth: 0,
  },
  /* --- New Side Panel Preview Dashboard Layout Sheets --- */
  templateSidePanel: {
    width: "390px",
    background: "#2b2b2bff",
    borderLeft: "1px solid #cbd5e1",
    display: "flex",
    flexDirection: "column",
    height: "100%",
    boxSizing: "border-box",
    animation: "slideIn 0.2s ease-out",
  },
  sidePanelHeader: {
    padding: "12px 16px",
    background: "#2b2b2bff",
    borderBottom: "1px solid #2b2b2bff",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  panelTitle: {
    margin: 0,
    fontSize: "14px",
    fontWeight: 600,
    color: "#ffffffff",
  },
  sidePanelScrollBody: {
    flex: 1,
    overflowY: "auto",
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  previewSpacing: {
    display: "flex",
    justifyContent: "center",
    width: "100%",
  },
  formCard: {
    background: "#ffffff",
    borderRadius: "8px",
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
  },
  sendBtn: {
    background: "#008069",
    borderColor: "#008069",
    height: "38px",
    fontWeight: "500",
    borderRadius: "6px",
    marginTop: "auto", // Forces the submit action element button layer flush to bottom row
  },
  empty: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    background: "#f8fafc",
    height: "100%",
  },
  emptyTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: "#94a3b8",
    margin: 0,
  },
  emptySub: {
    fontSize: 13,
    color: "#cbd5e1",
    margin: 0,
  },
};